'use client';

import { MouseEvent, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { LinkStyled } from '@components/common';
import { useAuthClient } from '@hooks/common/useAuthClient/useAuthClient';
import { useSwrApi } from '@hooks/useSwrApi';
import { VideoCameraIcon, XMarkIcon } from '@heroicons/react/20/solid';

// Session-scoped dismissal: cleared when the tab/session ends.
const DISMISS_KEY = 'my-interviews-banner-dismissed';

/**
 * @description MyInterviewsSection - small banner with the number of interviews waiting to be
 * finished. Links to the "My interviews" page. The count is the `total` from the
 * `/v1/my-job-interviews` endpoint (JobResult.total). Can be dismissed for the current session
 * via the close icon.
 */
export const MyInterviewsSection = ({ locale }: { locale: Locale }) => {
  const { t } = useTranslation('common');
  const { isAuth } = useAuthClient();

  // Dismissed-for-this-session flag (read once from sessionStorage).
  const [dismissed, setDismissed] = useState(
    () => typeof window !== 'undefined' && window.sessionStorage.getItem(DISMISS_KEY) === '1',
  );

  // Fetch only the count of interviews waiting (JobResult.total). limit=1 keeps the payload minimal.
  const { data } = useSwrApi<ApiSchema<'JobResult'>>({
    url: '/v1/my-job-interviews',
    urlParams: '?limit=1',
    locale,
    shouldFetch: isAuth && !dismissed,
    apiOptions: {
      revalidateOnFocus: false,
      dedupingInterval: 1000 * 60, // 1 minute
    },
  });

  const total = data?.total ?? 0;

  // Close the banner for the current session. Stop the click from following the wrapping link.
  const handleClose = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (typeof window !== 'undefined') window.sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  // Only show the banner when authenticated, not dismissed, and there are interviews waiting.
  if (!isAuth || dismissed || total === 0) {
    return null;
  }

  return (
    <LinkStyled
      styling="none"
      href="/my-job-interviews"
      className="flex items-center justify-between gap-3 rounded-2xl border border-secondary/40 bg-surface p-3 transition-colors hover:border-secondary md:p-4"
    >
      <span className="flex items-center gap-3">
        <span className="rounded-full bg-secondary/15 p-2 text-secondary">
          <VideoCameraIcon className="size-5" />
        </span>
        <span className="text-sm font-medium text-ink md:text-md">
          {t('my-interviews.banner').replace('{nr}', total.toString())}
        </span>
      </span>
      <button
        type="button"
        onClick={handleClose}
        aria-label={t('global.label.close', 'Close')}
        className="shrink-0 cursor-pointer rounded-full p-1 text-ink/50 transition-colors hover:bg-surface-100 hover:text-ink"
      >
        <XMarkIcon className="size-5" />
      </button>
    </LinkStyled>
  );
};
